import { useEffect, useMemo, useState } from "react"
import _ from "lodash"

import useRequest from "hooks/useRequest"
import {
  deleteDistribuitorOrderItem,
  getClients,
  getDistribuitorClientDailyAvailable,
  postDistribuitorClientOrdersBatch,
} from "helpers/backend_helper"

import {
  fechaDeHoy,
  fechaPorDefecto,
  partidasVivas,
} from "../Daily/useOrdersCreate"
import { opcionesFecha } from "../Daily/useProviderOrderForm"
import {
  MODO_GRANJA,
  MODO_REGION,
  ORDEN_CALIDAD,
  ORDEN_DISPONIBLE,
  ORDEN_PRECIO,
  PRODUCTO_TODOS,
} from "./static"
import { horaEfectiva, precioEnModo } from "./helpers"

/**
 * Convierte un renglón de `available_products` en una oferta del comparador,
 * con el precio de la región de entrega elegida. Devuelve null si el renglón
 * no tiene precio para esa región.
 */
const aOferta = (row, regionId) => {
  const precio = (row.prices || []).find(
    item => Number(item.region_id) === Number(regionId)
  )
  if (!precio) return null

  const pesoMin = Number(row.weight) || 0
  const pesoMax = Number(row.weight_max) || pesoMin

  return {
    id: row.id,
    priceId: precio.id,
    proveedor: row.provider?.company_name || "—",
    granja: row.farm?.name || "",
    producto: row.product,
    position: row.product_position,
    disponible: Number(row.available_product) || 0,
    pesoMin,
    pesoMax,
    pesoProm: (pesoMin + pesoMax) / 2,
    pigmento: Number(row.quality) || 0,
    calidad: Number(row.quality_2) || 0,
    salud: Number(row.quality_3) || 0,
    pRegion: Number(precio.unit_price_shipped) || 0,
    // Recoger en granja solo cuando la granja lo permite y tiene precio.
    pGranja: row.farm?.local_delivery ? Number(precio.unit_price_local) || 0 : 0,
  }
}

export default function useOrdersCreateV2() {
  const reqClientes = useRequest([])
  const reqDisponibilidad = useRequest(null)
  const reqGuardar = useRequest(null)

  const [clients, setClients] = useState([])
  const [clientId, setClientId] = useState(null)

  const [dates, setDates] = useState([])
  const [date, setDate] = useState(fechaDeHoy)
  const [addresses, setAddresses] = useState([])
  const [addressId, setAddressId] = useState(null)
  const [data, setData] = useState([])
  const [minutosCierre, setMinutosCierre] = useState(null)

  const [modo, setModo] = useState(MODO_REGION)
  const [producto, setProducto] = useState(PRODUCTO_TODOS)
  const [orden, setOrden] = useState(ORDEN_PRECIO)
  const [busqueda, setBusqueda] = useState("")
  const [diaAbierto, setDiaAbierto] = useState(true)

  // Borrador por oferta: { [id]: { qty, fecha, hora } }
  const [borrador, setBorrador] = useState({})

  useEffect(() => {
    reqClientes
      .req(getClients({ is_for_order: true }))
      .then(response =>
        setClients(
          _.orderBy(
            (response || []).filter(cliente => cliente.isActivated),
            ["company_name"]
          )
        )
      )
      .catch(() => {
        // api_helper ya notifica el error
      })
  }, [])

  const limpiarCliente = () => {
    setData([])
    setDates([])
    setDate(fechaDeHoy())
    setAddresses([])
    setAddressId(null)
    setMinutosCierre(null)
    setBorrador({})
  }

  /** Trae la disponibilidad del cliente. Solo fija fecha y dirección en la primera carga. */
  const cargarDisponibilidad = async (id, inicial) => {
    try {
      const response = await reqDisponibilidad.req(
        getDistribuitorClientDailyAvailable({ enterprise_id: id })
      )
      setData(response?.data || [])
      setMinutosCierre(
        response?.time === undefined ? null : Number(response.time) || 0
      )

      if (inicial) {
        const dias = response?.days || []
        setDates(dias)
        setDate(fechaPorDefecto(dias))

        const direcciones = response?.enterprise?.deliveries_address || []
        setAddresses(direcciones)
        setAddressId(direcciones[0] ? direcciones[0].id : null)
      }
    } catch (error) {
      // api_helper ya notifica el error
      if (inicial) limpiarCliente()
    }
  }

  const onChangeClient = id => {
    setClientId(id)
    setBorrador({})
    if (!id) {
      limpiarCliente()
      return
    }
    cargarDisponibilidad(id, true)
  }

  const onChangeDate = value => {
    setDate(value)
    setBorrador({})
  }

  const onChangeAddress = value => {
    setAddressId(value)
    setBorrador({})
  }

  const address = addresses.find(item => item.id === addressId) || null

  const ofertas = useMemo(
    () =>
      _.flatMap(data || [], grupo => grupo.rows || [])
        .filter(row => row.date === date)
        .map(row => aOferta(row, address?.region_id))
        .filter(oferta => oferta && oferta.disponible > 0),
    [data, date, address]
  )

  const enModo = m => ofertas.filter(oferta => precioEnModo(oferta, m) > 0)
  const pool = enModo(modo)
  const esLocal = modo === MODO_GRANJA

  const productos = useMemo(
    () =>
      _.uniqBy(_.orderBy(ofertas, ["position"]), "producto").map(
        oferta => oferta.producto
      ),
    [ofertas]
  )

  // Mejor (menor) precio por producto dentro del modo activo.
  const mejores = pool.reduce((acc, oferta) => {
    const precio = precioEnModo(oferta, modo)
    if (acc[oferta.producto] === undefined || precio < acc[oferta.producto]) {
      acc[oferta.producto] = precio
    }
    return acc
  }, {})

  const fechasRecepcion = opcionesFecha(date)

  /** Borrador completo de una oferta, con fecha y hora por defecto. */
  const borradorDe = oferta => {
    const actual = borrador[oferta.id] || {}
    const fecha =
      fechasRecepcion.find(opcion => opcion.value === actual.fecha)?.value ||
      fechasRecepcion[0]?.value ||
      date
    return {
      qty: actual.qty || "",
      fecha,
      hora: horaEfectiva(actual.hora, fecha, esLocal, date),
      fechaEditada: !!actual.fecha,
    }
  }

  const texto = busqueda.trim().toLowerCase()
  const filas = _.orderBy(
    pool
      .filter(
        oferta => producto === PRODUCTO_TODOS || oferta.producto === producto
      )
      .filter(
        oferta =>
          !texto ||
          `${oferta.proveedor} ${oferta.granja}`.toLowerCase().includes(texto)
      ),
    orden === ORDEN_DISPONIBLE
      ? [oferta => -oferta.disponible]
      : orden === ORDEN_CALIDAD
      ? [
          oferta => -(oferta.pigmento + oferta.calidad + oferta.salud),
          oferta => precioEnModo(oferta, modo),
        ]
      : [oferta => precioEnModo(oferta, modo), "proveedor"]
  ).map(oferta => {
    const precio = precioEnModo(oferta, modo)
    const draft = borradorDe(oferta)
    const cantidad = parseInt(draft.qty, 10) || 0
    return {
      ...oferta,
      ...draft,
      precio,
      cantidad,
      excede: cantidad > oferta.disponible,
      esMejor: precio === mejores[oferta.producto],
      delta: precio - mejores[oferta.producto],
      importe: cantidad * precio,
    }
  })

  // Renglones capturados en el modo activo, filtrados o no.
  const pendientes = pool
    .map(oferta => {
      const draft = borradorDe(oferta)
      return { oferta, draft, cantidad: parseInt(draft.qty, 10) || 0 }
    })
    .filter(item => item.cantidad > 0)

  const selPiezas = _.sumBy(pendientes, "cantidad")
  const selImporte = _.sumBy(
    pendientes,
    item => item.cantidad * precioEnModo(item.oferta, modo)
  )
  // Ahorro contra pagar el precio más caro del mismo producto.
  const peor = _.sumBy(pendientes, item => {
    const mismos = pool
      .filter(oferta => oferta.producto === item.oferta.producto)
      .map(oferta => precioEnModo(oferta, modo))
    return item.cantidad * Math.max(0, ...mismos)
  })
  const hayExcesos = pendientes.some(
    item => item.cantidad > item.oferta.disponible
  )
  const sinHora = pendientes.some(item => !item.draft.hora)

  // Partidas vivas del cliente para la fecha y dirección elegidas.
  const pedidosDia = _.flatMap(data || [], grupo => {
    const base = (grupo.rows || [])[0]
    return (grupo.orders || [])
      .filter(
        order =>
          order.order_date === date &&
          Number(order.address_id) === Number(address?.id)
      )
      .flatMap(order =>
        partidasVivas(order).map(item => ({
          id: item.id,
          proveedor: base?.provider?.company_name || "—",
          producto: item.product,
          piezas: Number(item.quantity) || 0,
          local: !Number(item.delivered),
          precio: Number(item.delivered)
            ? Number(item.unit_price_shipped) || 0
            : Number(item.unit_price) || 0,
          recepcion: item.delivery_date,
        }))
      )
  })

  const actualizarBorrador = (id, cambios) =>
    setBorrador(actual => ({
      ...actual,
      [id]: { ...(actual[id] || {}), ...cambios },
    }))

  const aPartida = ({ oferta, draft, cantidad }) => ({
    daily_availability_id: oferta.id,
    price_id: oferta.priceId,
    quantity: cantidad,
    local: esLocal,
    delivery_date: `${draft.fecha} ${draft.hora}`,
  })

  const enviar = async items => {
    if (!items.length || !clientId || !address || reqGuardar.isLoading) return
    try {
      await reqGuardar.req(
        postDistribuitorClientOrdersBatch({
          enterprise_id: clientId,
          date,
          address_id: address.id,
          items: items.map(aPartida),
        })
      )
      setBorrador(actual => {
        const siguiente = { ...actual }
        items.forEach(item => delete siguiente[item.oferta.id])
        return siguiente
      })
      await cargarDisponibilidad(clientId, false)
    } catch (error) {
      // api_helper ya notifica el error
    }
  }

  const puedeSolicitar =
    pendientes.length > 0 && !hayExcesos && !sinHora && !reqGuardar.isLoading

  const onSolicitar = () => {
    if (puedeSolicitar) enviar(pendientes)
  }

  const onAgregar = id => {
    const item = pendientes.find(pendiente => pendiente.oferta.id === id)
    if (
      !item ||
      item.cantidad > item.oferta.disponible ||
      !item.draft.hora ||
      reqGuardar.isLoading
    ) {
      return
    }
    enviar([item])
  }

  const onEliminarPartida = async itemId => {
    if (!clientId || reqGuardar.isLoading) return
    try {
      await reqGuardar.req(deleteDistribuitorOrderItem(itemId))
      await cargarDisponibilidad(clientId, false)
    } catch (error) {
      // api_helper ya notifica el error
    }
  }

  return {
    state: {
      isLoading: reqClientes.isLoading || reqDisponibilidad.isLoading,
      isSaving: reqGuardar.isLoading,
      clients,
      clientId,
      dates,
      date,
      addresses,
      addressId,
      address,
      minutosCierre,
      pedidosDia,
      diaAbierto,
      modo,
      countRegion: enModo(MODO_REGION).length,
      countGranja: enModo(MODO_GRANJA).length,
      productos,
      producto,
      orden,
      busqueda,
      filas,
      totalPool: pool.length,
      fechasRecepcion,
      esLocal,
      selCount: pendientes.length,
      selPiezas,
      selImporte,
      ahorro: Math.max(0, peor - selImporte),
      hayExcesos,
      puedeSolicitar,
      sinOfertas: !!(clientId && address && ofertas.length === 0),
    },
    events: {
      onChangeClient,
      onChangeDate,
      onChangeAddress,
      onToggleDia: () => setDiaAbierto(abierto => !abierto),
      onChangeModo: setModo,
      onChangeProducto: setProducto,
      onChangeOrden: setOrden,
      onChangeBusqueda: setBusqueda,
      onChangeCantidad: (id, qty) => actualizarBorrador(id, { qty }),
      onChangeFecha: (id, fecha) => actualizarBorrador(id, { fecha }),
      onChangeHora: (id, hora) => actualizarBorrador(id, { hora }),
      onAgregar,
      onSolicitar,
      onLimpiar: () => setBorrador({}),
      onEliminarPartida,
    },
  }
}
